/**
 * InitiativeOverviewForm - Reusable form for Initiative overview fields
 * 
 * Used in:
 * - InitiativeDetailModal (edit mode)
 * - InitiativeMergeModal (source view + merged form)
 * 
 * Props:
 * - formData: { title, description, status, owner_id, target_date, date_confidence, is_blocked, blocker_reason, follow_type }
 * - onChange: (field, value) => void
 * - isEditing: boolean (controls whether fields are editable)
 * - isDisabled: boolean (overrides isEditing for locked states)
 * - colors: theme colors from useSafeMode
 * - teamMembers: array of team members for owner dropdown
 * - parentDeadlines: array of {date, label} for calendar markers
 * - showProgressFollows: boolean (whether to show the Progress Follows field)
 * - onProgressFollowsChange: (type) => void (callback when changing follow type - may need confirmation)
 * - errors: { [field]: string } - validation errors to display
 * - mode: 'edit' | 'create' | 'view'
 */

import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import ReactMarkdown from 'react-markdown';
import { MarkdownEditor } from '../ui/markdown-editor';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { cn } from '../../lib/utils';
import { User, Calendar, AlertTriangle, AlertCircle } from 'lucide-react';

// Status options for initiatives
export const INITIATIVE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-500' },
  { value: 'planning', label: 'Planning', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-500' },
  { value: 'shipped', label: 'Shipped', color: 'bg-green-500' },
  { value: 'paused', label: 'Paused', color: 'bg-purple-500' },
];

// Confidence options for target date
export const CONFIDENCE_OPTIONS = [
  { value: 'high', label: 'High', color: 'text-green-600 dark:text-green-400' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600 dark:text-amber-400' },
  { value: 'low', label: 'Low', color: 'text-red-600 dark:text-red-400' },
];

// Progress follows options
export const FOLLOW_TYPE_OPTIONS = [
  { value: 'epics', label: 'Epics' },
  { value: 'tags', label: 'Tags' },
];

// Required fields for initiative
export const INITIATIVE_REQUIRED_FIELDS = ['title'];

const InitiativeOverviewForm = ({
  formData,
  onChange,
  isEditing = true,
  isDisabled = false,
  colors,
  teamMembers = [],
  parentDeadlines = [],
  showProgressFollows = true,
  onProgressFollowsChange,
  errors = {},
  mode = 'edit',
}) => {
  const handleChange = (field, value) => {
    if (onChange) {
      onChange(field, value);
    }
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field) => {
    return errors[field] ? 'border-red-500 ring-1 ring-red-500' : '';
  };

  const effectiveDisabled = isDisabled || !isEditing;
  const statusConfig = INITIATIVE_STATUS_OPTIONS.find(s => s.value === formData.status) || INITIATIVE_STATUS_OPTIONS[0];

  return (
    <div className="space-y-4">
      {/* Key Attributes Section */}
      <div className="space-y-4">
        {/* Row 1: Title | Owner */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <Label className={cn(colors.textSecondary, errors.title && "text-red-500")}>
              Title <span className="text-red-500">*</span>
            </Label>
            {isEditing && !isDisabled ? (
              <>
                <Input
                  value={formData.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Mobile App Launch"
                  disabled={effectiveDisabled}
                  className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, getFieldErrorClass('title'))}
                  data-testid="initiative-title-input"
                />
                {errors.title && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.title}
                  </p>
                )}
              </>
            ) : (
              <p className={cn("mt-1 p-2 rounded", colors.bgTertiary, colors.textPrimary)}>
                {formData.title || 'Not specified'}
              </p>
            )}
          </div>
          
          <div className="flex flex-col">
            <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
              <User className="w-3 h-3" /> Owner
            </Label>
            {isEditing && !isDisabled ? (
              <Select 
                value={formData.owner_id || 'none'} 
                onValueChange={(v) => handleChange('owner_id', v === 'none' ? '' : v)}
                disabled={effectiveDisabled}
              >
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {teamMembers.map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className={cn("mt-1", colors.textPrimary)}>
                {teamMembers.find(m => m.user_id === formData.owner_id)?.name || 'Unassigned'}
              </p>
            )}
          </div>
        </div>
        
        {/* Row 2: Status | Progress Follows | Target Date */}
        <div className={cn("grid gap-4", showProgressFollows ? "grid-cols-3" : "grid-cols-2")}>
          <div className="flex flex-col">
            <Label className={colors.textSecondary}>Status</Label>
            {isEditing ? (
              <Select 
                value={formData.status || 'draft'} 
                onValueChange={(v) => handleChange('status', v)}
                disabled={effectiveDisabled}
              >
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                  {INITIATIVE_STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <span className={cn("w-2 h-2 rounded-full", s.color)} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <span className={cn("w-2 h-2 rounded-full", statusConfig.color)} />
                <span className={colors.textPrimary}>{statusConfig.label}</span>
              </div>
            )}
          </div>
          
          {showProgressFollows && (
            <div className="flex flex-col">
              <Label className={colors.textSecondary}>Progress Follows</Label>
              {isEditing ? (
                <Select 
                  value={formData.follow_type || 'epics'} 
                  onValueChange={(v) => {
                    if (onProgressFollowsChange) {
                      onProgressFollowsChange(v);
                    } else {
                      handleChange('follow_type', v);
                    }
                  }}
                  disabled={effectiveDisabled}
                >
                  <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                    {FOLLOW_TYPE_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className={cn("mt-1", colors.textPrimary)}>
                  {FOLLOW_TYPE_OPTIONS.find(f => f.value === formData.follow_type)?.label || 'Epics'}
                </p>
              )}
            </div>
          )}
          
          <div className="flex flex-col">
            <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
              <Calendar className="w-3 h-3" /> Target Date
            </Label>
            {isEditing && !isDisabled ? (
              <div className="mt-1">
                <DatePickerWithMarkers
                  value={formData.target_date}
                  onChange={(date) => handleChange('target_date', date)}
                  parentDeadlines={parentDeadlines}
                  placeholder="Select target date"
                  disabled={effectiveDisabled}
                />
              </div>
            ) : (
              <p className={cn("mt-1", colors.textPrimary)}>
                {formData.target_date 
                  ? new Date(formData.target_date).toLocaleDateString()
                  : 'Not set'}
              </p>
            )}
          </div>
        </div>
        
        {/* Row 3: Date Confidence (only show if target date is set) */}
        {formData.target_date && (
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col">
              <Label className={colors.textSecondary}>Date Confidence</Label>
              {isEditing && !formData.is_blocked ? (
                <Select 
                  value={formData.date_confidence || 'medium'} 
                  onValueChange={(v) => handleChange('date_confidence', v)}
                  disabled={effectiveDisabled || formData.is_blocked}
                >
                  <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                    {CONFIDENCE_OPTIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className={c.color}>{c.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className={cn(
                  "mt-1",
                  formData.is_blocked 
                    ? "text-red-500" 
                    : CONFIDENCE_OPTIONS.find(c => c.value === formData.date_confidence)?.color || colors.textPrimary
                )}>
                  {formData.is_blocked ? 'Low (Blocked)' : CONFIDENCE_OPTIONS.find(c => c.value === formData.date_confidence)?.label || 'Medium'}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <div>
        <Label className={colors.textSecondary}>Description</Label>
        {isEditing && !isDisabled ? (
          <div className="mt-1" data-testid="initiative-description-input">
            <MarkdownEditor
              content={formData.description || ''}
              onChange={(value) => handleChange('description', value)}
              placeholder="Describe the initiative's purpose and scope..."
              minHeight={250}
              disabled={effectiveDisabled}
            />
          </div>
        ) : (
          <div className={cn("mt-1 p-3 rounded-lg", colors.bgTertiary)}>
            {formData.description ? (
              <div className={cn("prose prose-sm max-w-none dark:prose-invert", colors.textPrimary)}>
                <ReactMarkdown>{formData.description}</ReactMarkdown>
              </div>
            ) : (
              <p className={cn("text-sm", colors.textMuted)}>No description provided</p>
            )}
          </div>
        )}
      </div>

      {/* Blocker Section */}
      <div className={cn(
        "p-4 rounded-lg border",
        formData.is_blocked 
          ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700"
          : cn(colors.bgTertiary, colors.borderPrimary)
      )}>
        <div className="flex items-center justify-between">
          <Label className={cn("flex items-center gap-2", formData.is_blocked ? "text-amber-700 dark:text-amber-300" : colors.textPrimary)}>
            <AlertTriangle className={cn("w-4 h-4", formData.is_blocked && "text-amber-600")} />
            Blocked Status
          </Label>
          {isEditing && (
            <Switch
              checked={formData.is_blocked || false}
              onCheckedChange={(checked) => {
                handleChange('is_blocked', checked);
                if (checked) {
                  handleChange('date_confidence', 'low');
                }
              }}
              disabled={effectiveDisabled}
            />
          )}
        </div>
        
        {formData.is_blocked && (
          <div className="mt-3">
            <Label className={cn("text-sm", colors.textSecondary)}>Blocker Reason</Label>
            {isEditing ? (
              <Textarea
                value={formData.blocker_reason || ''}
                onChange={(e) => handleChange('blocker_reason', e.target.value)}
                placeholder="Describe what's blocking this initiative..."
                rows={2}
                disabled={effectiveDisabled}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            ) : (
              <p className={cn("mt-1 text-sm", colors.textPrimary)}>
                {formData.blocker_reason || 'No reason provided'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InitiativeOverviewForm;
